import {
  OpmSampleWaterfallInstrumentDto,
  OpmSampleWaterfallInstrumentTranchesDto,
  OpmSampleWaterfallsDto,
  OpmWaterfallConditionDto,
} from '@app/shared/models/contracts/opm-calculation-results-dto';

/**
 * Sentinel returned by a value selector when the row it belongs to is not part of the
 * scenario (column) being rendered. The table renders every row required by *any* scenario,
 * so cells for scenarios that do not require that row are rendered empty and highlighted.
 */
export const NOT_APPLICABLE = '__sample-waterfall-not-applicable__';

export type NotApplicable = typeof NOT_APPLICABLE;

export type WaterfallCellValue = string | number | boolean | undefined | null | NotApplicable;

export const isNotApplicable = (value: WaterfallCellValue): boolean => value === NOT_APPLICABLE;

export type IdentifiedInstrument = OpmSampleWaterfallInstrumentDto & { instrumentId: string };

/** All instruments present in at least one scenario, keeping first-seen ordering. */
export const getInstrumentsAcrossScenarios = (
  waterfallResults: Dictionary<OpmSampleWaterfallsDto>,
  columnKeys: string[]
): IdentifiedInstrument[] => {
  const instruments = new Map<string, IdentifiedInstrument>();

  columnKeys.forEach((columnKey) => {
    Object.entries(waterfallResults[columnKey]?.instruments ?? {}).forEach(
      ([instrumentId, instrument]) => {
        if (!instruments.has(instrumentId)) {
          instruments.set(instrumentId, { ...instrument, instrumentId });
        }
      }
    );
  });

  return Array.from(instruments.values());
};

/** All tranche keys used by an instrument in at least one scenario, keeping first-seen ordering. */
export const getTrancheKeysAcrossScenarios = (
  waterfallResults: Dictionary<OpmSampleWaterfallsDto>,
  columnKeys: string[],
  instrumentId: string
): string[] => {
  const trancheKeys: string[] = [];

  columnKeys.forEach((columnKey) => {
    Object.keys(waterfallResults[columnKey]?.instruments?.[instrumentId]?.tranches ?? {}).forEach(
      (trancheKey) => {
        if (!trancheKeys.includes(trancheKey)) {
          trancheKeys.push(trancheKey);
        }
      }
    );
  });

  return trancheKeys;
};

export const getTrancheForColumn = (
  waterfallResults: Dictionary<OpmSampleWaterfallsDto>,
  columnKey: string,
  instrumentId: string,
  trancheKey: string
): OpmSampleWaterfallInstrumentTranchesDto | undefined =>
  waterfallResults[columnKey]?.instruments?.[instrumentId]?.tranches?.[trancheKey];

/**
 * The conditions of a tranche can differ between scenarios, so rows are driven by the
 * longest set of conditions found and each condition is described by the first scenario
 * that contains it.
 */
export const getConditionsAcrossScenarios = (
  waterfallResults: Dictionary<OpmSampleWaterfallsDto>,
  columnKeys: string[],
  instrumentId: string,
  trancheKey: string
): OpmWaterfallConditionDto[] => {
  const conditions: OpmWaterfallConditionDto[] = [];

  columnKeys.forEach((columnKey) => {
    const columnConditions =
      getTrancheForColumn(waterfallResults, columnKey, instrumentId, trancheKey)?.conditions ?? [];
    columnConditions.forEach((condition, index) => {
      if (!conditions[index]) {
        conditions[index] = condition;
      }
    });
  });

  return conditions;
};

/** As above, but for the ordinary equity (TOE) conditions held against a tranche. */
export const getOrdinaryEquityConditionsAcrossScenarios = (
  waterfallResults: Dictionary<OpmSampleWaterfallsDto>,
  columnKeys: string[],
  instrumentId: string,
  trancheKey: string
): OpmWaterfallConditionDto[] => {
  const conditions: OpmWaterfallConditionDto[] = [];

  columnKeys.forEach((columnKey) => {
    const columnConditions =
      getTrancheForColumn(waterfallResults, columnKey, instrumentId, trancheKey)
        ?.ordinaryEquityMetrics?.conditions ?? [];
    columnConditions.forEach((condition, index) => {
      if (!conditions[index]) {
        conditions[index] = condition;
      }
    });
  });

  return conditions;
};
